<template>
    <div class="tw:min-h-screen tw:bg-zinc-50" :style="{ '--shop-primary': webShopState.primaryColor }">
        <!-- Hero -->
        <WebshopHero>
            <div class="tw:md:col-span-2">
                <ShopTitle></ShopTitle>
                <Filter v-model="webShopState.selectedCategoryId"
                        :nullLabel="$t('Anbefalte')" :filterValues="selectableCategories" />
                <div v-if="webShopState.selectedCategoryId" class="tw:mt-4 tw:border-t tw:border-zinc-200">
                    <Filter
                        v-model="webShopState.selectedProductTypeId"
                        :filterValues="selectableProductTypes"
                    />
                </div>
            </div>
            <div
                v-if="webShopState.promoContent"
                class="tw:rounded-2xl tw:border tw:border-zinc-200 tw:bg-white tw:p-4 tw:shadow-sm tw:text-zinc-600"
            >
                <div v-html="webShopState.promoContent"></div>
            </div>
        </WebshopHero>

        <!-- Produktgrid -->
        <main class="tw:mx-auto tw:max-w-7xl tw:p-4 tw:min-h-[40vh] tw:relative" id="api-shop-product-grid">
            <LoadingSpinner :isLoading="loadingShop" />

            <TransitionGroup
                tag="div"
                class="tw:grid tw:grid-cols-1 tw:gap-4 tw:sm:grid-cols-2 tw:lg:grid-cols-3"
                :name="animateProducts ? 'current-product' : 'plain'"
            >
                <div v-for="p in selectedProducts" :key="p.id" class="tw:lg:col-span-3">
                    <component
                        v-if="p.id"
                        :is="getCardComponent(p)"
                        :item="p"
                        :showWizard="true"
                    />
                </div>
            </TransitionGroup>
            <TransitionGroup
                tag="div"
                class="tw:grid tw:grid-cols-1 tw:gap-4 tw:sm:grid-cols-2 tw:lg:grid-cols-3"
                :name="animateProducts ? 'product-list' : 'plain'"
            >
                <div v-for="p in displayedProducts" :key="p.id">
                    <component
                        v-if="p.id"
                        :is="getCardComponent(p)"
                        :item="p"
                        :showWizard="false"
                    />
                </div>
            </TransitionGroup>

            <!-- Info-seksjon -->
            <section class="tw:mt-10 tw:grid tw:gap-4 tw:md:grid-cols-3">
                <div class="tw:rounded-2xl tw:border-zinc-200 tw:border tw:bg-white tw:p-5">
                    <h3 class="tw:mb-1 tw:font-semibold">{{ $t("Book kjøretimer i appen") }}</h3>
                    <p class="tw:text-sm tw:text-zinc-600">
                        {{
                            $t(
                                "Visste du at du også kan booke i appen? Etter fullført bestilling kan du logge inn og følge opplæringen din."
                            )
                        }}
                    </p>
                </div>
                <div class="tw:rounded-2xl tw:border-zinc-200 tw:border tw:bg-white tw:p-5">
                    <h3 class="tw:mb-1 tw:font-semibold">{{ $t("Spørsmål?") }}</h3>
                    <p class="tw:text-sm tw:text-zinc-600">
                        {{ contactOfficeString }}
                    </p>
                </div>
            </section>

            <footer class="tw:mt-12 tw:border-zinc-200 tw:border-t tw:py-8 tw:text-xs tw:text-zinc-500">
                © Lime Green Digital AS
            </footer>
            <div class="tw:max-h-[60px] tw:h-[60px] tw:space-x-2 tw:hidden"></div>
        </main>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import ProductTypes from "../../../productTypes";
import ShopTitle from "./title.vue";
import Filter from "./filter.vue";
import ProductOther from "./product-other.vue";
import ProductCourseGroup from "./product-course-group.vue";
import ProductElearning from "./product-elearning.vue";
import ProductPackage from "./product-package.vue";
import ProductLesson from "./product-lesson.vue";
import axios from "axios";
import LoadingSpinner from "../optima/wizard/loadingSpinner.vue";
import WebshopHero from "./WebshopHero.vue";
import "./webshop.tailwind.css";

import { useWebShopStore } from "./webshopState";
const webShopState = useWebShopStore();

// Local state
const loadingShop = ref(false);
const displayedProducts = ref([]);
const animateProducts = ref(false);

// Computed values
const selectedProductOfficeId = computed(() => webShopState.selectedProduct ? webShopState.selectedProduct.id : null)
const selectedCategoryId = computed(() => webShopState.selectedCategoryId);
const selectedProductTypeId = computed(() => webShopState.selectedProductTypeId);
const selectedProducts = computed(() => webShopState.selectedProduct ? [webShopState.selectedProduct] : []);

const contactOfficeString = computed(() => {
    if (webShopState.officeData.email && webShopState.officeData.mobile) {
        return window.TABS.$t("Kontakt oss på {officeEmail} eller {officeMobile}.", {
            officeEmail: webShopState.officeData.email,
            officeMobile: webShopState.officeData.mobile,
        });
    } else if (webShopState.officeData.email) {
        return window.TABS.$t("Kontakt oss på {officeEmail}.", {
            officeEmail: webShopState.officeData.email,
        });
    } else if (webShopState.officeData.mobile) {
        return window.TABS.$t("Kontakt oss på {officeMobile}.", {
            officeMobile: window.TABS.L10n.mobile.parse(webShopState.officeData.mobile).format(),
        });
    }
    return window.TABS.$t("Ta kontakt med oss, så hjelper vi deg.");
});

const selectableProductTypes = computed(() => {
    return filterByCategoryId.value.map((p) => {
        const t = webShopState.productTypes.find((t) => t.id === p.typeId)
        return {
            id: t.id,
            name: t.name,
        }
    }).reduce((acc, curr) => {
        if (!acc.find((item) => item.id === curr.id)) {
            acc.push(curr);
        }
        return acc;
    }, []);
});
const selectableCategories = computed(() => {
    // List of unique categories based on products in the selected product type
    return webShopState.products
        .map((c) => ({
            id: c.categoryId,
            name: c.categoryName,
        }))
        .reduce((acc, curr) => {
            if (!acc.find((item) => item.id === curr.id)) {
                acc.push(curr);
            }
            return acc;
        }, []);
});

const filterByCategoryId = computed(() => {
    let filteredProducts = webShopState.products.filter((p) =>
        // Filter match on category, if product type is selected we show all categories within that type - else we default to isRecommended status products
        webShopState.selectedCategoryId ? p.categoryId === webShopState.selectedCategoryId : (webShopState.selectedProductTypeId ? true : p.isRecommended)
    );
    if (!webShopState.selectedCategoryId && !filteredProducts.length) {
        filteredProducts = filteredProducts.slice(0, 5);
    }
    return filteredProducts;
})

const filteredByProductType = computed(() => {
    let filteredProducts = webShopState.products.filter((p) =>
        webShopState.selectedProductTypeId ? p.typeId === webShopState.selectedProductTypeId : p.isRecommended
    );
    if (!webShopState.selectedProductTypeId && !filteredProducts.length) {
        filteredProducts = filteredProducts.slice(0, 5);
    }
    return filteredProducts;
});

const filtered = computed(() => {
    return filterByCategoryId.value
        .filter((p) =>
            webShopState.selectedProductTypeId ? p.typeId === webShopState.selectedProductTypeId : true
        )
        .reduce((acc, curr) => {
            // Only push unique product office ids to filtered list - some may repeat across categories
            if (!acc.find((i) => i.id === curr.id)) {
                acc.push(curr);
            }
            return acc;
        }, []);
});

const getCardComponent = (item) => {
    const courseTypes = [ProductTypes.KURS, ProductTypes.GRUPPLEKTION, ProductTypes.KURS_RISK_BUNDLE];
    const lessonTypes = [ProductTypes.KJORETIME, ProductTypes];
    if (item.typeId === ProductTypes.PAKKE) return ProductPackage;
    if (item.typeId === ProductTypes.ELEARNING) return ProductElearning;
    if (courseTypes.indexOf(item.typeId) !== -1) return ProductCourseGroup;
    if (lessonTypes.indexOf(item.typeId) !== -1) return ProductLesson;
    if (item.typeId === ProductTypes.ANNET) return ProductOther;
    return ProductOther;
};

const getShopData = async () => {
    const endpoint = window.TABS.api.getEndPoint("shop");
    loadingShop.value = true;
    try {
        const { data, status } = await axios.get(endpoint, {
            withCredentials: true,
            params: {
                officeId: TABS.api.officeId,
                format: "json",
            },
        });
        loadingShop.value = false;
        if (status < 200 || status >= 300) throw new Error(`HTTP ${status}`);
        return data;
    } catch (err) {
        loadingShop.value = false;
        console.error("Failed to load shop data", err);
        return null;
    }
};

onMounted(async () => {
    try {
        let payload = window.shopData || null;
        if (!payload) {
            payload = await getShopData();
        }

        webShopState.isOptima = payload.isOptima;
        webShopState.domain = payload.domain || window.location.hostname;
        webShopState.products = payload.products;
        webShopState.productTypes = payload.types;
        webShopState.officeData.email = payload.office ? payload.office.email : "";
        webShopState.officeData.mobile = payload.office ? payload.office.mobile : "";
        webShopState.promoContent = payload.promoContent || "";
        webShopState.hasVueBookingWizard = payload.hasVueBookingWizard || false;
        webShopState.nnidPlaceholder = payload.nnidPlaceholder || '';

        if (payload.categoryId) {
            webShopState.selectedCategoryId = payload.categoryId;
        }
        if (payload.primaryColor) {
            webShopState.primaryColor = payload.primaryColor;
        }
    } catch (err) {
        console.error("Failed to load shop data", err);
        webShopState.products = [];
        webShopState.productTypes = [];
    }
});

watch(selectedProductOfficeId, (val) => {
    if (val === undefined) {
        // Initial load value, don't animate
        return;
    }
    animateProducts.value = true
    if (val) {
        displayedProducts.value = [];
    } else {
        displayedProducts.value = filtered.value;
    }
})

watch(selectedCategoryId, () => {
    webShopState.selectedProductTypeId = null;
})
// Disable animations when filters change (type/category) so list updates instantly
watch([selectedProductTypeId, selectedCategoryId], () => {
    animateProducts.value = false;
});

watch(filtered, () => {
    displayedProducts.value = filtered.value;
    webShopState.selectProduct(null)
    webShopState.selectCourse(null)
});
</script>

<style scoped></style>
